import React, { useMemo, useCallback, useRef, ReactNode, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';

import { KidsProfileFormProps, KidsProfileFormData } from './profile-form.models';
import { Form, IFormImperativeHandleProps } from '../../../../../common/form';
import { TextField } from '../../../../../common/form/text-field';
import { Button } from '../../../../../common/button-new';
import { useStyles } from './profile-form.styles';
import { Birthday } from '../birthday';
import { ExternalLinks } from '../../../../../../constants';
import { LensesExperience } from '../../../../../../constants/profile';
import { Experience } from '../experience';
import { Checkbox } from '../../../../../common/form/checkbox';
import { useGoBack } from '../../../../../../hooks/use-go-back';
import { InternalLinks } from '../../../../../../constants';

type FormValues = KidsProfileFormData & {
  dateOfBirth: Date | null;
  clExperience: string;
  consent: boolean;
};

export const ProfileForm = ({ onSubmit, validationSchema }: KidsProfileFormProps): JSX.Element => {
  const classes = useStyles();
  const intl = useIntl();
  const formRef = useRef<IFormImperativeHandleProps>(null);
  const [disabled, setDisabled] = useState<boolean>(true);
  const [formValues, setFormValues] = useState<{
    experience?: LensesExperience, date?: Date }>({ date: undefined, experience: undefined });
  const [isLeavePrompt, setIsLeavePrompt] = useState<boolean>(true);
  const isFormValidRef = useRef<boolean>(false);

  const defaultValues = useMemo(() => ({
    firstName: '',
    lastName: '',
    dateOfBirth: null,
    clExperience: '',
    consent: false,
  }), []);

  const handleSetBirthday = (date: Date) => {
    formRef.current?.setValue('dateOfBirth', date, { shouldValidate: true });
    setFormValues({ ...formValues, date });
  };

  const handleSetExperience = (experience: LensesExperience) => {
    formRef.current?.setValue('clExperience', experience, { shouldValidate: true });
    setFormValues({ ...formValues, experience: experience });
  };

  const updateSubmitState = (values?: Partial<FormValues>): void => {
    const currentValues = (values ?? formRef.current?.getValues()) as FormValues | undefined;
    const firstName = currentValues?.firstName?.trim();
    const lastName = currentValues?.lastName?.trim();
    const dateOfBirth = currentValues?.dateOfBirth;
    const clExperience = currentValues?.clExperience;
    const consent = currentValues?.consent;

    const allFieldsFilled = Boolean(firstName && lastName && dateOfBirth && clExperience && consent);

    setDisabled(!isFormValidRef.current || !allFieldsFilled);
  };

  const handleValidateDependencies = (isValid: boolean): void => {
    isFormValidRef.current = isValid;
    updateSubmitState();
  };

  const handleChangeDependencies = (values?: Partial<FormValues>): void => {
    updateSubmitState(values);
  };

  const handleSubmit = useCallback((values: KidsProfileFormData) => {
    setIsLeavePrompt(false);
    onSubmit({ ...values } as KidsProfileFormData);
  }, [onSubmit]);

  const { goBack } = useGoBack();
  const navigate = useNavigate();

  const handleReset = () => {
    setIsLeavePrompt(false);

    setTimeout(() => {
      if (window?.history?.state?.idx?.length) {
        goBack(!!window?.history?.state?.idx?.length);
      } else {
        navigate(InternalLinks.home);
      }
    }, 0);
  };

  return (
    <Form
      onSubmit={handleSubmit}
      withLeavePrompt={isLeavePrompt}
      validationSchema={validationSchema}
      defaultValues={defaultValues}
      ref={formRef}
      onValidateDependencies={handleValidateDependencies}
      onChangeValuesDependencies={handleChangeDependencies}
      validationMode="all"
    >
      <TextField
        name="firstName"
        fullWidth
        label={<FormattedMessage id="profile.nameTextField.label" />}
        isRequired
        autoComplete="off"
      />
      <TextField
        name="lastName"
        fullWidth
        label={<FormattedMessage id="profile.surnameTextField.label" />}
        isRequired
        autoComplete="off"
      />
      <Birthday onSetDate={handleSetBirthday} date={formValues?.date} />
      <Experience onSetValue={handleSetExperience} value={formValues?.experience} />
      <Checkbox
        name="consent"
        label={intl.formatMessage(
          { id: 'createKidsProfile.disclaimer' },
          {
            a: (chunk: ReactNode) => <a target="_blank" href={ExternalLinks.myAcuvueProgramPolicyLink}>{chunk}</a>,
            noBreak: <>&nbsp;</>,
            span: (chunk: ReactNode) => <span className={classes.asterisk}>{chunk}</span>,
          },
        )}
        required
      />
      <div className={classes.actions}>
        <Button
          type="submit"
          style="dark"
          data-testid="profile-submit"
          disabled={disabled}
        >
          <FormattedMessage id="common.addButton.label" />
        </Button>
        <Button style="outlined" onClick={handleReset} type="button">
          <FormattedMessage id="common.cancelButton.label" />
        </Button>
      </div>
    </Form>
  );
};
